import { chain, createToggler, disable, enable } from '@leprecoins/effector';
import { notifications } from '@leprecoins/effector-notifications';
import { clamp } from '@mantine/hooks';
import { combine, createEffect, createEvent, createStore, sample } from 'effector';
import { changeLanguage, t } from 'i18next';
import { and, combineEvents, empty, equals, not, spread } from 'patronum';
import {
	getLastBetQuery,
	getSettingsQuery,
	receiveBalance,
	receiveBet,
	receiveRefresh,
	receiveSession,
	receiveTurn,
	sendBet,
	Settings,
	TURN_STATUSES,
	TurnStatus,
	socket,
	isWebsocketErrorWithPayload,
} from 'shared/api';
import { CRITICAL_ERROR_CODES } from 'shared/config';
import { breakpoints } from 'shared/lib';
import {
	ONBOARDING_STEPS,
	ONBOARDING_STEPS_DESKTOP,
	ONBOARDING_STEPS_MOBILE,
	OnboardingStep,
} from '../config';

const errorModalToggler = createToggler();

const $settings = createStore<Settings | null>(null);

const $wager = createStore('0');

const $maxWin = $settings.map((settings) => settings?.maxWin ?? 0);

const $maxBet = $settings.map((settings) => settings?.maxBet ?? 0);

const $minBet = $settings.map((settings) => settings?.minBet ?? 0);

const $currency = $settings.map((settings) => settings?.currency ?? '');

const $multipliers = createStore<number[] | null>(null);

const $returnUrl = $settings.map((settings) => settings?.returnUrl ?? '');

const $username = $settings.map((settings) => settings?.username ?? '');

const $turnStatus = createStore<TurnStatus | null>(null);

const $balance = createStore(0);

const $clientSeed = createStore('clientSeed');

const $fastBets = $settings.map((settings) => settings?.fastBets ?? []);

const $sendBetPending = createStore(false);

const $broke = combine([$wager, $balance], ([wager, balance]) => Number(wager) > balance);

const $brokeCanVisible = createStore(true);

const $isOnboarding = createStore(false);

const $brokeVisible = and(
	$broke,
	$brokeCanVisible,
	not(equals($turnStatus, TURN_STATUSES.bet)),
	not($isOnboarding),
);

const $onboardingStep = createStore<OnboardingStep>(ONBOARDING_STEPS.GAME_FIELD);

const $isBetButtonOnboarding = and(
	$isOnboarding,
	equals($onboardingStep, ONBOARDING_STEPS.BET_BUTTON),
);

const win = createEvent();

const gameFieldAnimated = createEvent();

const changeOnboardingStep = createEvent<OnboardingStep>();

const changeNextOnboardingStep = createEvent();

const skipOnboarding = createEvent();

const assetsLoaded = createEvent();

const loaderClosed = createEvent();

const changeLanguageFx = createEffect((lng: string) => {
	changeLanguage(lng.toLowerCase());
});

const receiveInsufficientBalance = receiveBalance.success.filter({
	fn: ({ isBalanceInsufficient }) => !!isBalanceInsufficient,
});

const receiveSessionExpired = receiveBalance.success.filter({
	fn: ({ isSessionExpired }) => !!isSessionExpired,
});

sample({
	clock: $settings,
	fn: (settings) => settings?.multipliers[0] ?? null,
	target: $multipliers,
});

sample({
	clock: receiveSession.success,
	target: [getSettingsQuery.start, getLastBetQuery.start],
});

sample({
	clock: getSettingsQuery.finished.success,
	fn: ({ result }) => result,
	target: $settings,
});

sample({
	clock: receiveBalance.success,
	fn: ({ balance }) => balance,
	target: $balance,
});

sample({
	clock: receiveInsufficientBalance,
	fn: () => t('errors:insufficient-balance'),
	target: notifications.showError,
});

sample({
	clock: receiveSessionExpired,
	fn: () => t('errors:session-expired'),
	target: [notifications.showError, errorModalToggler.enable],
});

chain(sendBet, $turnStatus.reinit);

sample({
	clock: [
		receiveBet.success.map(() => TURN_STATUSES.bet),
		receiveTurn.success.map(({ status }) => status),
		receiveRefresh.success.map(({ status }) => status),
	],
	target: $turnStatus,
});

sample({
	clock: combineEvents([getSettingsQuery.finished.success, getLastBetQuery.finished.success]),
	filter: empty($turnStatus),
	fn: ([{ result: settings }, { result: lastBet }]) =>
		`${clamp(lastBet?.bet ?? 0, settings.minBet, settings.maxBet)}`,
	target: $wager,
});

sample({
	clock: combineEvents([getSettingsQuery.finished.success, receiveRefresh.success]),
	fn: ([{ result: settings }, { wager, clientSeed }]) => ({
		wager: `${clamp(wager, settings.minBet, settings.maxBet)}`,
		clientSeed,
	}),
	target: spread({
		wager: $wager,
		clientSeed: $clientSeed,
	}),
});

enable(sendBet, $sendBetPending);

sample({
	clock: [receiveBet.success, receiveBet.failure],
	fn: () => false,
	target: $sendBetPending,
});

chain(changeOnboardingStep, $onboardingStep);

sample({
	clock: changeNextOnboardingStep,
	source: $onboardingStep,
	filter: (step) => step === ONBOARDING_STEPS.BET_BUTTON,
	target: skipOnboarding,
});

sample({
	clock: changeNextOnboardingStep,
	source: [$onboardingStep, breakpoints.sm.$matches],
	filter: ([step]) => step !== ONBOARDING_STEPS.BET_BUTTON,
	fn: ([step, sm]) => {
		const stepsConfig = !sm ? ONBOARDING_STEPS_MOBILE : ONBOARDING_STEPS_DESKTOP;

		const stepKeys = Object.keys(stepsConfig) as Array<keyof typeof stepsConfig>;
		const currentIndex = stepKeys.findIndex((key) => stepsConfig[key] === step);
		const nextKey = stepKeys[currentIndex + 1];

		return stepsConfig[nextKey];
	},
	target: $onboardingStep,
});

disable(skipOnboarding, $isOnboarding);

sample({
	clock: breakpoints.sm.$matches,
	source: {
		active: $isOnboarding,
		step: $onboardingStep,
	},
	filter: ({ active, step }) =>
		active && step !== ONBOARDING_STEPS.GAME_FIELD && step !== ONBOARDING_STEPS.BET_BUTTON,
	fn: (_, matches) =>
		matches ? ONBOARDING_STEPS_DESKTOP.BET_MENU : ONBOARDING_STEPS_MOBILE.WAGER_FIELD,
	target: $onboardingStep,
});

sample({
	clock: receiveTurn.success,
	source: $balance,
	filter: not(equals($turnStatus, TURN_STATUSES.bet)),
	fn: (balance, { profit }) => balance + profit,
	target: $balance,
});

sample({
	clock: [receiveRefresh.success, sendBet],
	fn: () => false,
	target: $brokeCanVisible,
});

sample({
	clock: receiveBet.success,
	fn: ({ balance }) => balance,
	target: $balance,
});

sample({
	clock: getSettingsQuery.finished.success,
	fn: ({ result: { language } }) => language.toLowerCase(),
	target: changeLanguageFx,
});

enable(gameFieldAnimated, $brokeCanVisible);

sample({
	clock: socket.disconnected,
	fn: () => t('errors:network'),
	target: notifications.showError,
});

sample({
	clock: socket.receiveAny.failure,
	filter: (error) =>
		isWebsocketErrorWithPayload(error) && CRITICAL_ERROR_CODES.includes(error.payload.code[0]),
	target: errorModalToggler.enable,
});

export const gameModel = {
	$currency,
	$balance,
	$wager,
	$settings,
	$turnStatus,
	$multipliers,
	$returnUrl,
	$maxWin,
	$maxBet,
	$minBet,
	$username,
	$clientSeed,
	$fastBets,
	$sendBetPending,
	$broke,
	$brokeVisible,
	assetsLoaded,
	gameFieldAnimated,
	win,
	$isOnboarding,
	$onboardingStep,
	$isBetButtonOnboarding,
	changeOnboardingStep,
	skipOnboarding,
	changeNextOnboardingStep,
	loaderClosed,
	receiveInsufficientBalance,
	receiveSessionExpired,
	errorModalToggler,
};
